import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CONTENTFUL_CONFIG } from './contentful.config';

export interface ContentfulSys {
  id: string;
  type: string;
}

export interface ContentfulEntry<TFields> {
  sys: ContentfulSys;
  fields: TFields;
}

export interface ContentfulCollection<TFields> {
  total: number;
  skip: number;
  limit: number;
  items: Array<ContentfulEntry<TFields>>;
}

export interface GetEntriesOptions {
  contentType?: string;
  locale?: string;
  limit?: number;
  skip?: number;
  query?: Record<string, string | number | boolean | undefined>;
}

@Injectable({ providedIn: 'root' })
export class ContentfulService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CONTENTFUL_CONFIG);

  getEntries<TFields = Record<string, unknown>>(
    options: GetEntriesOptions = {},
  ): Observable<ContentfulCollection<TFields>> {
    let params = new HttpParams();

    if (options.contentType) params = params.set('content_type', options.contentType);
    if (options.locale) params = params.set('locale', options.locale);
    if (options.limit !== undefined) params = params.set('limit', options.limit);
    if (options.skip !== undefined) params = params.set('skip', options.skip);

    if (options.query) {
      for (const [key, value] of Object.entries(options.query)) {
        if (value !== undefined) {
          params = params.set(key, String(value));
        }
      }
    }

    const target = this.readTarget();
    return this.http.get<ContentfulCollection<TFields>>(
      `${target.baseUrl}/entries`,
      { headers: target.headers, params },
    );
  }

  getEntryById<TFields = Record<string, unknown>>(
    entryId: string,
    locale?: string,
  ): Observable<ContentfulEntry<TFields>> {
    let params = new HttpParams();
    if (locale) params = params.set('locale', locale);

    const target = this.readTarget();
    return this.http.get<ContentfulEntry<TFields>>(
      `${target.baseUrl}/entries/${entryId}`,
      { headers: target.headers, params },
    );
  }

  private readTarget(): { baseUrl: string; headers: HttpHeaders } {
    return {
      baseUrl: `https://cdn.contentful.com/spaces/${this.config.spaceId}/environments/${this.config.environment}`,
      headers: new HttpHeaders({ Authorization: `Bearer ${this.config.deliveryToken}` }),
    };
  }
}
